import IDVerifiedChip from '@/components/ui/IDVerifiedChip'
import { formatDate, formatDateTime } from '@/lib/utils'
import type { PatientListItem } from '@/services/patients'
import { Box } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

export type PatientRow = Omit<PatientListItem, 'lastContact' | 'dateOfBirth'> & {
  dateOfBirth: Date | null
  lastContact: Date | null
}

export const columns: GridColDef<PatientRow>[] = [
  {
    field: 'firstName',
    headerName: 'First name',
    flex: 1,
    minWidth: 160
  },
  {
    field: 'lastName',
    headerName: 'Last name',
    flex: 1,
    minWidth: 160
  },
  {
    field: 'gender',
    headerName: 'Gender',
    flex: 1,
    minWidth: 140,
    valueFormatter: value => {
      if (!value) return ''
      const gender = String(value)
      return gender.charAt(0).toUpperCase() + gender.slice(1)
    }
  },
  {
    field: 'idVerified',
    headerName: 'ID verified',
    type: 'boolean',
    minWidth: 180,
    renderCell: (params: GridRenderCellParams) =>
      params.value ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            p: 2
          }}
        >
          <IDVerifiedChip />
        </Box>
      ) : null
  },
  {
    field: 'dateOfBirth',
    headerName: 'Date of birth',
    flex: 1,
    minWidth: 160,
    type: 'date',
    valueFormatter: value => (value ? formatDate(value) : '')
  },
  {
    field: 'postcode',
    headerName: 'Postcode',
    flex: 1,
    minWidth: 140,
    // Not in the backend's sortBy allowlist (SORT_BY_ATTRIBUTES)
    sortable: false
  },
  {
    field: 'lastContact',
    headerName: 'Last updated on',
    flex: 1,
    minWidth: 200,
    type: 'dateTime',
    valueFormatter: value => formatDateTime(value)
  }
]

/**
 * Fields offered in the filter popover without being rendered as grid columns.
 * Address 1/2 filter the list via the backend address join but only the
 * postcode is worth a column.
 */
export const PATIENT_FILTER_ONLY_COLUMNS: GridColDef[] = [
  { field: 'email', headerName: 'Email' },
  { field: 'address1', headerName: 'Address 1' },
  { field: 'address2', headerName: 'Address 2' }
]
